/**
 * Product Alt — collapsible tabs.
 *
 * Two behaviours requested for the "Collapsible row" blocks on the Product Alt section
 * (sections/main-product-alt.liquid, block type `collapsible_tab`), matching what
 * <product-faq> already does for the PDP FAQ accordions (assets/product-faq-accordions.js):
 *
 *   1. Animated open/close. <details> cannot transition its own height, so the
 *      `.accordion__reveal` wrapper is animated and the element's `open` is set around
 *      it. Reopening mid-collapse resumes from the current height instead of snapping.
 *   2. One tab open at a time — opening one closes any other open tab in the same
 *      product info column.
 *
 * Scoped to `.product__accordion` details that carry the `.accordion__reveal` wrapper,
 * so it only ever touches markup rendered by main-product-alt.liquid. With JS off, or
 * before this runs, the native <details> still opens and closes.
 */
(function () {
  'use strict';

  // Section can repeat on one page (e.g. a page with two Product Alt blocks); the
  // script tag then repeats too, so guard against binding the click listener twice.
  if (window.__productAccordionAltBound) return;
  window.__productAccordionAltBound = true;

  var ANIM = { min: 200, max: 420, perPx: 0.35, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' };
  var GROUP_SELECTOR = 'section[id^="ProductInfo-"]';
  var ITEM_SELECTOR = '.product__accordion > details';

  function reducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function duration(px) {
    return Math.min(ANIM.max, Math.max(ANIM.min, px * ANIM.perPx));
  }

  function revealOf(item) {
    return item.querySelector('.accordion__reveal');
  }

  function groupOf(item) {
    return item.closest(GROUP_SELECTOR) || document;
  }

  function itemsIn(group) {
    return Array.prototype.slice.call(group.querySelectorAll(ITEM_SELECTOR)).filter(revealOf);
  }

  // Only the animation that still owns the element cleans up, so a queued cancel
  // cannot strip a newer run.
  function stopAnim(item) {
    if (item.paAnim) item.paAnim.cancel();
  }

  function slide(item, from, to, onDone, onAbort) {
    var box = revealOf(item);
    if (reducedMotion() || !box || !box.animate) {
      if (onDone) onDone();
      return;
    }

    box.classList.add('accordion__reveal--animating');
    var run = box.animate([{ height: from + 'px' }, { height: to + 'px' }], {
      duration: duration(Math.abs(to - from)),
      easing: ANIM.easing,
    });
    item.paAnim = run;

    var release = function () {
      if (item.paAnim !== run) return;
      item.paAnim = null;
      box.classList.remove('accordion__reveal--animating');
    };
    run.onfinish = function () {
      release();
      if (onDone) onDone();
    };
    run.oncancel = function () {
      release();
      if (onAbort) onAbort();
    };
  }

  function open(item) {
    var box = revealOf(item);
    // Reopening mid-collapse resumes from wherever the content is, not from 0.
    var from = item.open && box ? box.getBoundingClientRect().height : 0;
    stopAnim(item);
    item.paClosing = false;
    item.open = true;
    if (box) slide(item, from, box.scrollHeight);

    itemsIn(groupOf(item)).forEach(function (other) {
      if (other !== item && other.open) close(other);
    });
  }

  function close(item) {
    if (!item.open || item.paClosing) return;
    var box = revealOf(item);
    var from = box ? box.getBoundingClientRect().height : 0;
    stopAnim(item);
    item.paClosing = true;
    slide(
      item,
      from,
      0,
      function () {
        item.paClosing = false;
        item.open = false;
      },
      function () {
        item.paClosing = false;
      }
    );
  }

  function onClick(event) {
    var summary = event.target.closest ? event.target.closest('.product__accordion > details > summary') : null;
    if (!summary) return;
    var item = summary.parentNode;
    if (!revealOf(item)) return;

    // Drive open/close here so a collapse can animate before <details> hides its content.
    event.preventDefault();
    if (item.open && !item.paClosing) {
      close(item);
    } else {
      open(item);
    }
  }

  document.addEventListener('click', onClick);
})();
